import { CustomModal, FormEntry, FormSelect, FormCheck } from "components";
import { Controller } from "react-hook-form";
import Select from "react-select";
import { useEffect, useState, useMemo, useCallback } from "react";
import { formatGroupLabel, initialFormState } from "./constants";
import { Row, Col, Button } from "reactstrap";
import apiService from "services/api";
import { useNotificationContext } from "contexts";
import { useForm } from "react-hook-form";
import { timeDifference } from "utils/timeDifference";
import moment from "moment";
import { useQuery } from "react-query";
import { lessonCategories } from "pages/Course/constants";
import { capitalize } from "utils/capitalize";
import { centreOptions } from "constants";
import useSelectLessonModal, { sortLessonRecords } from "./SelectLessonModal";
import useSelectTeacherModal from "./SelectTeacherModal";
import { useDebounce } from "usehooks-ts";

export const FormGroup = ({
  children,
  formLabel,
  customEndComponent,
  error,
  light = false,
  width = "w-100",
}) => (
  <div className={`mb-3 ${width}`}>
    <div
      className={`${light && "text-dark"} text-left ml-2 mb-1 text-light font-weight-bold d-flex justify-content-between align-items-center`}
    >
      {formLabel}
      {customEndComponent}
    </div>
    {children}
    {error && (
      <div className="text-center">
        <small className="text-danger">{error?.message}</small>
      </div>
    )}
  </div>
);

const NewEventModal = ({
  studentOptions,
  teacherOptions,
  open,
  hide,
  slotInfo,
  refetchEvents,
  isFetchingLessonRecords,
}) => {
  const { createNotification } = useNotificationContext();
  const {
    handleSubmit,
    setValue,
    watch,
    register,
    reset,
    control,
    getValues,
    formState: { errors, dirtyFields, isDirty },
  } = useForm({
    defaultValues: initialFormState,
    mode: "onChange",
  });

  const [isCustomLessonEntry, setCustomLessonEntry] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const selectedCourse = watch("course");

  const homeworkTime = watch("homeworkTime");

  const [startTime, endTime, date, teacher, lesson] = watch([
    "startTime",
    "endTime",
    "date",
    "teacher",
    "lesson",
  ]);

  const homeworkTimeDuration = watch("homeworkTimeDuration");

  const selectedStudents = watch("studentIds");

  const isRecurring = watch("isRecurring");

  const { data: selectedStudentsData, isFetching: isStudentFetching } =
    useQuery(
      ["selectedStudentsData", selectedStudents],
      () =>
        Promise.all(
          selectedStudents.map((student) =>
            apiService.student.get(student.value),
          ),
        ),
      {
        enabled: Boolean(selectedStudents?.length),
      },
    );

  const { data: studentCourseOptions, isFetching: isStudentCourseFetching } =
    useQuery(
      ["studentCourse", selectedStudents],
      () => {
        const studentCourses = selectedStudentsData.map((student) =>
          student?.courses?.map((course) => ({
            ...course.courseDetail,
            hoursLeft: course.hoursLeft,
          })),
        );
        setValue("lesson", undefined);

        const intersectedCourses = studentCourses
          .map((studentCourse) =>
            studentCourse.filter((course) =>
              studentCourses
                .map((studentCourse) =>
                  studentCourse.some((course_1) => course_1.id === course.id),
                )
                .every((isTrue) => isTrue),
            ),
          )
          .flat()
          .filter(
            (course, index, self) =>
              index === self.findIndex((course_1) => course_1.id === course.id),
          );
        return intersectedCourses.map((course) => ({
          label: `${course.name} [${course.hoursLeft}hours]`,
          value: course.id,
          hoursLeft: course.hoursLeft,
        }));
      },
      { enabled: Boolean(selectedStudents?.length) && !isStudentFetching },
    );

  const completedLessons = useMemo(
    () =>
      selectedStudentsData
        ? selectedStudentsData?.reduce(
            (accumulated, student) => {
              const records = student?.lessonRecords
                ?.filter(
                  (lessonRecord) =>
                    lessonRecord?.lessonDetail?.lessonId &&
                    !lessonRecord?.lessonDetail?.lesson?.isCustomLesson,
                )
                .map((lessonRecord) => ({
                  ...lessonRecord?.lessonDetail?.lesson,
                  date: lessonRecord?.lessonDetail?.date,
                }));
              accumulated.ids = accumulated.ids.concat(
                records.map((record) => record.id),
              );
              accumulated.records = accumulated.records.concat(records);
              accumulated.customRecords = student?.lessonRecords
                ?.filter(
                  (lessonRecord) =>
                    lessonRecord?.lessonDetail?.lesson?.isCustomLesson &&
                    lessonRecord?.lessonDetail?.lessonId,
                )
                .map((lessonRecord) => {
                  return {
                    ...lessonRecord?.lessonDetail?.lesson,
                    date: lessonRecord?.lessonDetail?.date,
                  };
                });
              return accumulated;
            },
            { ids: [], records: [], customRecords: [] },
          )
        : { ids: [], records: [], customRecords: [] },
    [selectedStudentsData],
  );

  const legacyCompletedLessons = useMemo(
    () =>
      selectedStudentsData
        ? selectedStudentsData?.reduce(
            (accumulated, student) => {
              accumulated.ids = accumulated.ids.concat(
                student?.legacyCompletedLessonRecords?.map(
                  (lessonRecord) => lessonRecord?.lessonId,
                ),
              );
              accumulated.records = accumulated.records.concat(
                student?.legacyCompletedLessonRecords.map((lessonRecord) => ({
                  ...lessonRecord?.lesson,
                  date: lessonRecord?.lessonDetail?.date,
                })),
              );
              return accumulated;
            },
            { ids: [], records: [] },
          )
        : { ids: [], records: [] },
    [selectedStudentsData],
  );

  const { data: lessonOptions } = useQuery(
    ["lesson", selectedCourse?.value],
    () =>
      selectedCourse
        ? apiService.course.get(selectedCourse?.value).then((course) => {
            const lessonsByCategory = lessonCategories.map((category) => ({
              label: capitalize(category),
              options: course.lessons
                ?.filter((lesson) => {
                  return (
                    !completedLessons.ids.includes(lesson.id) &&
                    !legacyCompletedLessons.ids.includes(lesson.id)
                  );
                })
                .map((lesson) => ({
                  value: lesson,
                  label: `${lesson.code} - ${lesson.name}`,
                }))
                .filter((lesson) => lesson?.value?.category === category),
            }));
            return lessonsByCategory;
          })
        : Promise.resolve([]),
    { enabled: !!selectedCourse?.value && !isStudentCourseFetching },
  );

  const incrementLessonEndTime = useCallback((duration) => {
    const newTime = moment(getValues("endTime"), "HH:mm:ss")
      .add(duration, "minutes")
      .format("HH:mm:ss");
    setValue("endTime", newTime);
  }, []);

  useEffect(() => {
    if (!dirtyFields.homeworkTime) return;
    if (homeworkTime) {
      setValue("homeworkTimeDuration", "15");
      incrementLessonEndTime(15);
    } else if (homeworkTime == false) {
      incrementLessonEndTime(-getValues("homeworkTimeDuration"));
      setValue("homeworkTimeDuration", undefined);
    }
  }, [homeworkTime]); // so that the homeworktime does not affect if not checked before

  const addNewEvent = (data) => {
    setIsLoading(true);
    const { lesson, course, teacher, studentIds, observerIds } = data;
    const formData = {
      ...data,
      ...(lesson && { lessonId: lesson.id }),
      ...(course && { courseId: course.value }),
      ...(teacher && { teacherId: teacher.id }),
      isRecurring,
      ...(isCustomLessonEntry && {
        customLessonName: data.customLessonName,
      }),
      isCustomLesson: isCustomLessonEntry,
      studentIds: studentIds.map((student) => student.value),
      observerIds: observerIds.map((observer) => observer?.value?.id),
    };

    return apiService.lessonRecord
      .create(formData)
      .then(() => {
        createNotification({
          type: "success",
          message: "Successfully added lesson record",
        });
        refetchEvents();
        resetAndHide();
      })
      .catch((error) =>
        createNotification({
          type: "danger",
          message: `Failed to add lesson record: ${error.message}`,
        }),
      )
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (slotInfo) {
      setValue("startTime", slotInfo.start);
      setValue("endTime", slotInfo.end);
      setValue("date", slotInfo.date);
    }
  }, [slotInfo]);

  const resetAndHide = () => {
    reset(initialFormState);
    hide();
  };

  const [SelectLessonModal, selectLessonModalState, selectLessonModalDispatch] =
    useSelectLessonModal();
  const [SelectTeacherModal, _, selectTeacherModalDispatch] =
    useSelectTeacherModal({ date, startTime, endTime });

  const { data: availableTeachers, isLoading: isLoadingAvailableTeachers } =
    useQuery(
      ["availability", "lesson", startTime, endTime, date],
      () =>
        apiService.staff.availability.get(undefined, {
          start_time: startTime,
          end_time: endTime,
          selected_date: date,
        }), //queryParams are camelcased
      { enabled: !!(date && endTime && startTime) },
    );

  const sortedLessonRecords = useMemo(
    () =>
      sortLessonRecords([
        ...completedLessons.records,
        ...legacyCompletedLessons.records,
      ]),
    [completedLessons.records, legacyCompletedLessons.records],
  );

  const sortedCustomRecords = useMemo(
    () => sortLessonRecords(sortLessonRecords(completedLessons.customRecords)),
    [completedLessons.customRecords],
  );
  return (
    <>
      <form onSubmit={handleSubmit(addNewEvent)}>
        <SelectLessonModal
          sortedLessonRecords={sortedLessonRecords}
          sortedCustomRecords={sortedCustomRecords}
          errors={errors}
          setCustomLessonEntry={setCustomLessonEntry}
          isCustomLessonEntry={isCustomLessonEntry}
          register={register}
          control={control}
          lessonOptions={lessonOptions}
          isStudentCourseFetching={isStudentCourseFetching}
          formatGroupLabel={formatGroupLabel}
        />
        <SelectTeacherModal
          errors={errors}
          control={control}
          availableTeachers={availableTeachers}
          date={date}
          startTime={startTime}
          endTime={endTime}
          teacherOptions={teacherOptions}
        />
        <CustomModal
          show={open}
          title="New Lesson"
          hideModal={resetAndHide}
          light={true}
          triggerAlertWhenClose={isDirty}
          triggerAlertMessageWhenClose="Are you sure you want to close? Form entries will be reset."
          buttons={
            <div className="d-flex justify-between">
              <button
                className="btn btn-primary"
                type="submit"
                disabled={isLoading || isFetchingLessonRecords}
              >
                Add
              </button>
              <button
                className="btn btn-secondary"
                onClick={resetAndHide}
                disabled={isFetchingLessonRecords}
              >
                Cancel
              </button>
            </div>
          }
        >
          <Row className="align-items-center">
            <Col lg={6}>
              <FormEntry
                formLabel="Lesson Date"
                register={register}
                name="date"
                type="date"
                required={true}
              />
            </Col>
            <Col lg={6}>
              <FormSelect
                formLabel="Centre"
                register={register}
                name="centre"
                error={errors.centre}
                options={centreOptions}
                required={true}
              />
            </Col>
          </Row>

          <Row className="align-items-center">
            <Col lg={6}>
              <FormEntry
                formLabel="Start Time"
                register={register}
                name="startTime"
                type="time"
                step="60"
                error={errors.startTime}
                required={true}
              />
            </Col>
            <Col lg={6}>
              <FormEntry
                formLabel="End Time"
                register={register}
                name="endTime"
                type="time"
                validation={{
                  validate: {
                    checkMinMax: (preparationEndTime) =>
                      timeDifference(
                        getValues("startTime"),
                        preparationEndTime,
                      ) > 0 || "End time must be after start time",
                  },
                }}
                error={errors.endTime}
                step="60"
                required={true}
              />
            </Col>
            <Col sm={6}>
              <div className="d-flex">
                <FormCheck
                  formLabel="Homework Time"
                  register={register}
                  name="homeworkTime"
                  error={errors.homeworkTime}
                  required={true}
                />
                {homeworkTime && (
                  <Row className="ml-2">
                    <Col>
                      <Controller
                        control={control}
                        name="homeworkTimeDuration"
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <FormGroup width="w-auto" error={error}>
                            <input
                              type="radio"
                              onChange={(e) => {
                                incrementLessonEndTime(
                                  -value + +e.target.value,
                                );
                                onChange(e.target.value);
                              }}
                              checked={value == "15"}
                              value="15"
                            />
                            <small className="text-primary ml-1">15m</small>
                          </FormGroup>
                        )}
                      />
                    </Col>
                    <Controller
                      control={control}
                      name="homeworkTimeDuration"
                      render={({
                        field: { value, onChange },
                        fieldState: { error },
                      }) => (
                        <FormGroup width="w-auto" error={error}>
                          <input
                            type="radio"
                            onChange={(e) => {
                              incrementLessonEndTime(-value + +e.target.value);
                              onChange(e.target.value);
                            }}
                            checked={value == "30"}
                            value="30"
                          />
                          <small className="text-primary ml-1">30m</small>
                        </FormGroup>
                      )}
                    />
                  </Row>
                )}
              </div>
            </Col>
            <Col sm={6}>
              <FormCheck
                formLabel="Urgent Class?"
                register={register}
                name="isUrgentClass"
                error={errors.isUrgentClass}
                required={true}
              />
            </Col>
            <Col sm={6}>
              <FormCheck
                formLabel="Trial Class?"
                register={register}
                name="isTrialClass"
                error={errors.isTrialClass}
                required={true}
              />
            </Col>
            <Col sm={6} md={4}>
              <FormCheck
                formLabel="Revision Test"
                register={register}
                name="isTest"
                error={errors.isTest}
              />
            </Col>
          </Row>
          <Row>
            <div className="col-12">
              <FormEntry
                formLabel="Location (Room)"
                register={register}
                name="location"
                type="text"
                error={errors.location}
                placeholder="eg. CP401, room D"
              />
            </div>

            <div className="col-12">
              <FormEntry
                formLabel="Remark"
                register={register}
                name="remark"
                type="text"
                error={errors.remark}
                placeholder="eg. lesson materials or special requests by student"
              />
            </div>
          </Row>

          <div className="mt-5">
            <div className="text-secondary font-weight-bold display-5 mb-4 text-center">
              Lesson Details
            </div>
            <FormGroup error={errors.studentIds} formLabel="Student">
              <Controller
                control={control}
                rules={{
                  required: {
                    value: true,
                    message: "Student is required",
                  },
                }}
                defaultValue={studentOptions?.map((c) => c.value)}
                name="studentIds"
                render={({ field: { onChange, value, ref } }) => (
                  <Select
                    inputRef={ref}
                    className="react-select info"
                    classNamePrefix="react-select"
                    value={studentOptions?.filter((c) =>
                      value.map((v) => v.value).includes(c.value),
                    )}
                    onChange={(val) => {
                      onChange(val ? val : []);
                      setValue("course", null);
                    }}
                    isClearable={false}
                    options={studentOptions || []}
                    isMulti
                    placeholder="Select student...."
                  />
                )}
              />
            </FormGroup>
            <FormGroup error={errors.course} formLabel="Course">
              <Controller
                control={control}
                name="course"
                rules={{
                  required: {
                    value: true,
                    message: "Add a course",
                  },
                }}
                render={({ field: { onChange, value, ref } }) => (
                  <Select
                    inputRef={ref}
                    className="react-select info"
                    classNamePrefix="react-select"
                    key={JSON.stringify(value)}
                    value={studentCourseOptions?.find(
                      (c) => c.value === value?.value,
                    )}
                    onChange={(val) => {
                      onChange(val);
                      setValue("lesson", null);
                    }}
                    placeholder="Select course...."
                    isDisabled={isStudentFetching}
                    options={studentCourseOptions || []}
                  />
                )}
              />
            </FormGroup>
            <FormGroup error={errors.lesson} formLabel="Lesson">
              <Button
                color="transparent"
                type="button"
                outline={true}
                disabled={!lessonOptions}
                onClick={() => selectLessonModalDispatch({ type: "OPEN" })}
              >
                Select Lesson
              </Button>
              <span className="ml-2 text-info">
                {lesson?.name || watch("customLessonName")}
              </span>
            </FormGroup>

            <FormGroup error={errors.teacher} formLabel="Teacher">
              <Button
                color="transparent"
                type="button"
                outline={true}
                onClick={() => selectTeacherModalDispatch({ type: "OPEN" })}
              >
                Select Teacher
              </Button>
              <span className="ml-2 text-info">{teacher?.fullName}</span>
            </FormGroup>
            {/* <FormGroup error={errors.teacher} formLabel="Teacher">
							<Controller
								control={control}
								name="teacher"
								render={({ field: { onChange, value, ref } }) => (
									<Select
										inputRef={ref}
										className="react-select info"
										classNamePrefix="react-select"
										key={value}
										value={teacherOptions?.find((c) => c.value?.id === value?.id)}
										onChange={(val) => onChange(val.value)}
										placeholder="Select teacher...."
										options={teacherOptions || []}
									/>
								)}
							/>
						</FormGroup> */}
            <FormGroup error={errors.observerIds} formLabel="Observer">
              <Controller
                control={control}
                defaultValue={teacherOptions?.map((c) => c.value)}
                name="observerIds"
                render={({ field: { onChange, value, ref } }) => (
                  <Select
                    inputRef={ref}
                    className="react-select info"
                    classNamePrefix="react-select"
                    value={teacherOptions?.filter((c) =>
                      value.map((v) => v.value).includes(c.value),
                    )}
                    onChange={(val) => {
                      onChange(val ? val : []);
                    }}
                    isClearable={false}
                    options={teacherOptions || []}
                    isMulti
                    placeholder="Select teachers as observer...."
                  />
                )}
              />
            </FormGroup>
            <div className="mt-4">
              <FormCheck
                formLabel="Is Recurring"
                register={register}
                name="isRecurring"
                error={errors.isRecurring}
                required={true}
              />
              {isRecurring && (
                <FormEntry
                  register={register}
                  name="recurrEndDate"
                  formLabel="Recurr Untill"
                  disabled={!selectedCourse?.value}
                  type="date"
                  error={errors.recurrEndDate}
                  validation={{
                    validate: {
                      checkLessThan: (recurrEndDate) =>
                        moment(recurrEndDate).diff(moment(getValues("date"))) >=
                          0 ||
                        "Recurring End Date must be after recurring start date",
                      checkMoreThanAllowedHours: (recurrEndDate) => {
                        const daysToRecur = Math.floor(
                          moment
                            .duration(
                              moment(recurrEndDate).diff(
                                moment(getValues("date")),
                              ),
                            )
                            .asDays() / 7,
                        );
                        const classDuration =
                          timeDifference(
                            getValues("startTime"),
                            getValues("endTime"),
                          ) / 60;
                        return (
                          (daysToRecur + 1) * classDuration <=
                            getValues("course")?.hoursLeft ||
                          "Not enough hours to recur"
                        );
                      },
                    },
                  }}
                />
              )}
            </div>
          </div>
        </CustomModal>
      </form>
    </>
  );
};

export default NewEventModal;
